// AP Statistics Curriculum - Units 7-9
import { Unit } from '../components/GradeLessonPage';

export const unit7: Unit = {
  id: 'unit7',
  title: 'Unit 7: Inference for Quantitative Data: Means',
  description: 'Confidence intervals and significance tests for means (t-procedures)',
  weight: '10%–18%',
  topics: [
    {
      id: 'ci-mean',
      title: 'Confidence Intervals for Means',
      description: 'One-sample and two-sample t-intervals',
      skills: [
        {
          id: 'one-mean-ci',
          name: 'One-Sample t-Interval for Mean',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `ci1m-${i + 1}`,
            question: i === 0 ? 'Sample of 16 batteries: x̄=52 hours, s=8 hours. Construct 95% CI for true mean lifetime. (t*=2.131 for df=15)' :
                     i === 1 ? 'Why do we use t-distribution instead of z-distribution when σ is unknown?' :
                     i === 2 ? 'Sample: n=25, x̄=75, s=12. Calculate 90% CI for population mean. (t*=1.711)' :
                     `Construct ${['95%', '90%', '99%'][i % 3]} t-interval for mean with n=${Math.floor(Math.random() * 20) + 15}.`,
            answer: i === 0 ? 'n=16, df=15, x̄=52, s=8, t*=2.131\nSE = s/√n = 8/√16 = 8/4 = 2\n95% CI: 52 ± 2.131(2) = 52 ± 4.262 = (47.738, 56.262)\nWe are 95% confident the true mean battery lifetime is between 47.7 and 56.3 hours.' :
                   i === 1 ? 'When σ is unknown, we estimate it with s (sample SD). This adds extra variability because s varies from sample to sample. The t-distribution accounts for this extra uncertainty with heavier tails than the z-distribution. As n increases, t approaches z.' :
                   i === 2 ? 'SE = 12/√25 = 12/5 = 2.4\n90% CI: 75 ± 1.711(2.4) = 75 ± 4.106 = (70.894, 79.106)\nWe are 90% confident the true population mean is between 70.9 and 79.1.' :
                   'Solution with t-interval calculation and interpretation.',
            steps: [
              'Check conditions: Random, 10%, Normal/Large Sample (n≥30 or population normal)',
              'Calculate x̄ and s',
              'Calculate SE = s/√n',
              'Find t* from t-table with df = n-1',
              'CI: x̄ ± t*·SE',
              'Interpret in context'
            ]
          }))
        },
        {
          id: 'two-mean-ci',
          name: 'Two-Sample t-Interval for Difference in Means',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `ci2m-${i + 1}`,
            question: i === 0 ? 'Group 1: n₁=20, x̄₁=85, s₁=10. Group 2: n₂=18, x̄₂=78, s₂=12. Construct 95% CI for μ₁-μ₂. (Use t*=2.03, df=35)' :
                     i === 1 ? 'A CI for μ₁-μ₂ is (2.5, 8.3). Interpret this interval.' :
                     i === 2 ? 'New method: n=15, x̄=92, s=8. Old method: n=15, x̄=87, s=10. Test if new method better using 90% CI. (t*=1.701, df=27)' :
                     `Construct CI for difference in means with n₁=${Math.floor(Math.random() * 15) + 15}, n₂=${Math.floor(Math.random() * 15) + 15}.`,
            answer: i === 0 ? 'SE = √[(10²/20) + (12²/18)] = √[5 + 8] = √13 ≈ 3.606\n95% CI: (85-78) ± 2.03(3.606) = 7 ± 7.32 = (-0.32, 14.32)\nWe are 95% confident the true difference in means is between -0.32 and 14.32. Since 0 is in the interval, we cannot conclude the groups differ significantly.' :
                   i === 1 ? 'We are 95% confident that μ₁ exceeds μ₂ by between 2.5 and 8.3 units. Since the entire interval is positive (above 0), we have evidence that μ₁ > μ₂. Group 1 has a significantly higher mean than Group 2.' :
                   i === 2 ? 'SE = √[(8²/15) + (10²/15)] = √[4.27 + 6.67] = √10.94 ≈ 3.308\n90% CI: (92-87) ± 1.701(3.308) = 5 ± 5.63 = (-0.63, 10.63)\nSince 0 is in interval, insufficient evidence at 90% confidence that new method is better.' :
                   'Solution with two-sample t-interval.',
            steps: [
              'Check conditions for both groups',
              'Calculate SE = √[(s₁²/n₁) + (s₂²/n₂)]',
              'Use conservative df = smaller of (n₁-1, n₂-1) or calculator df',
              'CI: (x̄₁ - x̄₂) ± t*·SE',
              'If 0 in interval: no significant difference',
              'If 0 not in interval: significant difference'
            ]
          }))
        },
        {
          id: 'paired-t-ci',
          name: 'Paired t-Interval',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `pairt-${i + 1}`,
            question: i === 0 ? '10 people measured before and after treatment. Differences (after-before): x̄_d=5, s_d=3. Construct 95% CI for mean difference. (t*=2.262)' :
                     i === 1 ? 'When should you use paired t-procedures versus two-sample t-procedures?' :
                     i === 2 ? '8 students take test twice. Differences (Test2-Test1): 3,5,2,4,6,1,3,4. Construct 90% CI for mean improvement. (t*=1.895)' :
                     `Construct paired t-interval for ${Math.floor(Math.random() * 10) + 8} paired observations.`,
            answer: i === 0 ? 'n=10, df=9, x̄_d=5, s_d=3, t*=2.262\nSE = s_d/√n = 3/√10 ≈ 0.949\n95% CI: 5 ± 2.262(0.949) = 5 ± 2.146 = (2.854, 7.146)\nWe are 95% confident the true mean difference (improvement) is between 2.85 and 7.15 units.' :
                   i === 1 ? 'Use paired t when:\n- Same subjects measured twice (before/after)\n- Matched pairs (twins, husband/wife)\n- Natural pairing exists\nUse two-sample t when:\n- Independent groups with no natural pairing\nPaired is more powerful when appropriate because it controls for individual variation.' :
                   i === 2 ? 'Differences: 3,5,2,4,6,1,3,4. n=8, df=7\nx̄_d = (3+5+2+4+6+1+3+4)/8 = 28/8 = 3.5\ns_d = √[Σ(d-x̄_d)²/(n-1)] ≈ 1.69\nSE = 1.69/√8 ≈ 0.598\n90% CI: 3.5 ± 1.895(0.598) = 3.5 ± 1.133 = (2.367, 4.633)\nWe are 90% confident mean improvement is between 2.4 and 4.6 points.' :
                   'Solution with paired t-interval calculation.',
            steps: [
              'Calculate differences: d = (measurement 2) - (measurement 1)',
              'Find x̄_d and s_d from differences',
              'Check conditions on differences',
              'SE = s_d/√n where n = number of pairs',
              'CI: x̄_d ± t*·SE with df = n-1',
              'Interpret mean difference in context'
            ]
          }))
        }
      ]
    },
    {
      id: 'test-mean',
      title: 'Significance Tests for Means',
      description: 'One-sample, two-sample, and paired t-tests',
      skills: [
        {
          id: 'one-mean-test',
          name: 'One-Sample t-Test for Mean',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `test1m-${i + 1}`,
            question: i === 0 ? 'Company claims mean response time is 10 minutes. Sample of 25: x̄=11.5, s=3. Test if true mean differs from 10 at α=0.05.' :
                     i === 1 ? 'What is a P-value? How do you use it to make a decision?' :
                     i === 2 ? 'H₀: μ=50 vs Hₐ: μ>50. Sample: n=16, x̄=53, s=8. Calculate test statistic and P-value (one-tailed).' :
                     `Perform one-sample t-test with H₀: μ=${Math.floor(Math.random() * 30) + 50}.`,
            answer: i === 0 ? 'H₀: μ = 10 vs Hₐ: μ ≠ 10 (two-tailed)\nn=25, df=24, x̄=11.5, s=3\nSE = 3/√25 = 0.6\nt = (11.5-10)/0.6 = 1.5/0.6 = 2.5\nP-value = 2·P(t > 2.5) with df=24 ≈ 2(0.01) = 0.02\nSince P=0.02 < α=0.05, reject H₀. There is sufficient evidence that the true mean response time differs from 10 minutes.' :
                   i === 1 ? 'P-value is the probability of obtaining a test statistic as extreme or more extreme than observed, assuming H₀ is true. Small P-value = strong evidence against H₀.\nDecision rule: If P-value < α, reject H₀. If P-value ≥ α, fail to reject H₀.\nInterpretation: P=0.03 means "If H₀ were true, there\'s only a 3% chance of getting these results by random chance alone."' :
                   i === 2 ? 'SE = 8/√16 = 2\nt = (53-50)/2 = 1.5\nWith df=15, P-value = P(t > 1.5) ≈ 0.077 (one-tailed)\nModerate evidence against H₀ at α=0.10, but not at α=0.05.' :
                   'Solution with complete one-sample t-test.',
            steps: [
              'State hypotheses: H₀: μ = μ₀ vs Hₐ',
              'Check conditions',
              'Calculate t = (x̄ - μ₀) / (s/√n)',
              'Find P-value using t-distribution with df = n-1',
              'Compare P-value to α',
              'State conclusion in context'
            ]
          }))
        },
        {
          id: 'two-mean-test',
          name: 'Two-Sample t-Test for Difference in Means',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `test2m-${i + 1}`,
            question: i === 0 ? 'Test if Method A produces higher scores than Method B. A: n=20, x̄=85, s=12. B: n=18, x̄=78, s=10. α=0.05, use df=35.' :
                     i === 1 ? 'Why don\'t we pool standard deviations in two-sample t-test for means (unlike proportions)?' :
                     i === 2 ? 'Treatment: n=15, x̄=92, s=8. Control: n=15, x̄=87, s=10. Test if treatment significantly different at α=0.05 (two-tailed, df=27).' :
                     `Perform two-sample t-test comparing two groups.`,
            answer: i === 0 ? 'H₀: μ_A = μ_B vs Hₐ: μ_A > μ_B (one-tailed)\nSE = √[(12²/20) + (10²/18)] = √[7.2 + 5.56] = √12.76 ≈ 3.572\nt = (85-78)/3.572 = 7/3.572 ≈ 1.96\nP-value = P(t > 1.96) with df=35 ≈ 0.029\nSince P=0.029 < 0.05, reject H₀. There is sufficient evidence that Method A produces higher scores than Method B.' :
                   i === 1 ? 'We don\'t pool because:\n1) We\'re not assuming σ₁ = σ₂ (unlike proportions where H₀: p₁=p₂ implies common proportion)\n2) Population SDs may differ even if means are equal\n3) Using separate SDs is more robust and generally appropriate\nPooling is only justified with strong evidence that σ₁ = σ₂.' :
                   i === 2 ? 'H₀: μ₁ = μ₂ vs Hₐ: μ₁ ≠ μ₂\nSE = √[(8²/15) + (10²/15)] = √10.93 ≈ 3.31\nt = (92-87)/3.31 = 5/3.31 ≈ 1.51\nP-value = 2·P(t > 1.51) with df=27 ≈ 2(0.072) = 0.144\nP=0.144 > 0.05, fail to reject. Insufficient evidence of significant difference.' :
                   'Solution with two-sample t-test.',
            steps: [
              'State hypotheses: H₀: μ₁ = μ₂',
              'Check conditions for both groups',
              'Calculate SE = √[(s₁²/n₁) + (s₂²/n₂)]',
              'Calculate t = (x̄₁ - x̄₂) / SE',
              'Find P-value with appropriate df',
              'Make decision and conclude in context'
            ]
          }))
        },
        {
          id: 'paired-t-test',
          name: 'Paired t-Test',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `pairt-test-${i + 1}`,
            question: i === 0 ? '12 students trained, then tested. Mean improvement: x̄_d=4.5, s_d=2.5. Test if training effective (improvement > 0) at α=0.05.' :
                     i === 1 ? 'Explain why paired t-test is more powerful than two-sample t-test when data are paired.' :
                     i === 2 ? 'Blood pressure before/after medication for 10 patients. Differences (before-after): x̄_d=8, s_d=4. Test if medication reduces BP at α=0.01.' :
                     `Perform paired t-test for ${Math.floor(Math.random() * 8) + 10} paired observations.`,
            answer: i === 0 ? 'H₀: μ_d = 0 vs Hₐ: μ_d > 0 (one-tailed)\nn=12, df=11, x̄_d=4.5, s_d=2.5\nSE = 2.5/√12 ≈ 0.722\nt = (4.5-0)/0.722 ≈ 6.23\nP-value = P(t > 6.23) with df=11 ≈ 0.00003\nP < 0.05, reject H₀. Strong evidence that training is effective in improving scores.' :
                   i === 1 ? 'Paired t-test eliminates individual variation by comparing each subject to themselves. Differences remove person-to-person variability, focusing only on treatment effect. Two-sample test includes both treatment AND individual variation, making it harder to detect differences. Paired design controls for lurking variables naturally associated with individuals.' :
                   i === 2 ? 'H₀: μ_d = 0 vs Hₐ: μ_d > 0 (testing if before > after, so positive difference means reduction)\nn=10, df=9, x̄_d=8, s_d=4\nSE = 4/√10 ≈ 1.265\nt = (8-0)/1.265 ≈ 6.32\nP-value = P(t > 6.32) with df=9 < 0.001\nP < 0.01, reject H₀. Very strong evidence medication reduces blood pressure.' :
                   'Solution with paired t-test.',
            steps: [
              'Calculate differences for each pair',
              'State hypotheses about μ_d',
              'Check conditions on differences',
              'Calculate t = (x̄_d - 0) / (s_d/√n)',
              'Find P-value with df = n-1',
              'Conclude about mean difference'
            ]
          }))
        }
      ]
    }
  ]
};

export const unit8: Unit = {
  id: 'unit8',
  title: 'Unit 8: Inference for Categorical Data: Chi-Square',
  description: 'Chi-square tests for goodness-of-fit, homogeneity, and independence',
  weight: '2%–5%',
  topics: [
    {
      id: 'chi-square-tests',
      title: 'Chi-Square Tests',
      description: 'Goodness-of-fit, homogeneity, and independence tests',
      skills: [
        {
          id: 'chi-square-gof',
          name: 'Chi-Square Goodness-of-Fit Test',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `chigof-${i + 1}`,
            question: i === 0 ? 'Die rolled 60 times. Results: 1(8), 2(12), 3(9), 4(11), 5(10), 6(10). Test if die is fair at α=0.05.' :
                     i === 1 ? 'What are the conditions for chi-square goodness-of-fit test?' :
                     i === 2 ? 'Genetics predicts offspring ratio 9:3:3:1. Observed: 90,35,28,7 (n=160). Test at α=0.05.' :
                     `Perform goodness-of-fit test for ${Math.floor(Math.random() * 3) + 3} categories.`,
            answer: i === 0 ? 'H₀: Die is fair (each outcome has p=1/6) vs Hₐ: Not fair\nExpected for each: 60/6 = 10\nχ² = Σ[(O-E)²/E] = (8-10)²/10 + (12-10)²/10 + (9-10)²/10 + (11-10)²/10 + (10-10)²/10 + (10-10)²/10\n= 4/10 + 4/10 + 1/10 + 1/10 + 0 + 0 = 10/10 = 1.0\ndf = 6-1 = 5, P-value = P(χ² > 1.0) ≈ 0.96\nP > 0.05, fail to reject. No evidence die is unfair.' :
                   i === 1 ? 'Conditions:\n1) Random: Data from random sample/experiment\n2) 10%: n ≤ 10% of population\n3) Expected counts: All expected counts ≥ 5\n4) Independence: Individual observations are independent' :
                   i === 2 ? 'Expected: 9/16(160)=90, 3/16(160)=30, 3/16(160)=30, 1/16(160)=10\nχ² = (90-90)²/90 + (35-30)²/30 + (28-30)²/30 + (7-10)²/10\n= 0 + 0.833 + 0.133 + 0.9 = 1.866\ndf=3, P-value ≈ 0.60\nP > 0.05, consistent with 9:3:3:1 ratio.' :
                   'Solution with chi-square goodness-of-fit test.',
            steps: [
              'State H₀: specifies probability for each category',
              'Calculate expected counts: E = n·p',
              'Check all expected counts ≥ 5',
              'Calculate χ² = Σ[(O-E)²/E]',
              'df = (number of categories) - 1',
              'Find P-value, make decision'
            ]
          }))
        },
        {
          id: 'chi-square-independence',
          name: 'Chi-Square Test for Independence',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `chiind-${i + 1}`,
            question: i === 0 ? 'Test if gender and political party are independent. Male: Dem(40), Rep(35), Ind(25). Female: Dem(50), Rep(30), Ind(20). α=0.05.' :
                     i === 1 ? 'How do you calculate expected counts for a test of independence?' :
                     i === 2 ? 'Two-way table: Pass/Fail vs Study method A/B. A: Pass(60), Fail(40). B: Pass(45), Fail(55). Test independence at α=0.05.' :
                     `Test independence in ${Math.floor(Math.random() * 2) + 2} × ${Math.floor(Math.random() * 2) + 2} table.`,
            answer: i === 0 ? 'H₀: Gender and party are independent vs Hₐ: Not independent\nTotal: 200. Male: 100, Female: 100. Dem: 90, Rep: 65, Ind: 45\nExpected (Male, Dem) = (100)(90)/200 = 45\nExpected (Male, Rep) = (100)(65)/200 = 32.5\nExpected (Male, Ind) = (100)(45)/200 = 22.5\n(Same pattern for Female)\nχ² = (40-45)²/45 + (35-32.5)²/32.5 + ... ≈ 3.54\ndf = (2-1)(3-1) = 2, P-value ≈ 0.17\nP > 0.05, fail to reject. No evidence of association between gender and party.' :
                   i === 1 ? 'Expected count for cell (row i, column j):\nE = (row i total)(column j total) / (grand total)\nThis is the count expected if variables were independent (no association).' :
                   i === 2 ? 'Total: 200. A: 100, B: 100. Pass: 105, Fail: 95\nExpected: (A,Pass)=52.5, (A,Fail)=47.5, (B,Pass)=52.5, (B,Fail)=47.5\nχ² = (60-52.5)²/52.5 + (40-47.5)²/47.5 + (45-52.5)²/52.5 + (55-47.5)²/47.5\n= 1.07 + 1.18 + 1.07 + 1.18 = 4.50\ndf = 1, P-value ≈ 0.034\nP < 0.05, reject H₀. Evidence of association between study method and passing.' :
                   'Solution with test for independence.',
            steps: [
              'State H₀: Variables are independent',
              'Calculate expected: E = (row total)(col total)/(grand total)',
              'Check all expected counts ≥ 5',
              'Calculate χ² = Σ[(O-E)²/E] for all cells',
              'df = (rows-1)(columns-1)',
              'Find P-value and conclude'
            ]
          }))
        },
        {
          id: 'chi-square-homogeneity',
          name: 'Chi-Square Test for Homogeneity',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `chihom-${i + 1}`,
            question: i === 0 ? 'Compare satisfaction across 3 stores. Store A: Satisfied(45), Not(15). Store B: Satisfied(50), Not(10). Store C: Satisfied(40), Not(20). Test homogeneity at α=0.05.' :
                     i === 1 ? 'What is the difference between test for independence and test for homogeneity?' :
                     i === 2 ? 'Three diets tested. Diet 1: Success(30), Fail(20). Diet 2: Success(35), Fail(15). Diet 3: Success(25), Fail(25). Test if success rates equal at α=0.05.' :
                     `Test homogeneity across ${Math.floor(Math.random() * 2) + 3} groups.`,
            answer: i === 0 ? 'H₀: Satisfaction distribution same across stores vs Hₐ: Distributions differ\nTotal: 180. Satisfied: 135, Not: 45\nExpected (A, Satisfied) = 60(135)/180 = 45, (A, Not) = 15\n(Calculate for all cells)\nχ² = Σ[(O-E)²/E] ≈ 2.22\ndf = (3-1)(2-1) = 2, P-value ≈ 0.33\nP > 0.05, fail to reject. No evidence satisfaction differs across stores.' :
                   i === 1 ? 'Test for Independence:\n- One sample, classify by two variables\n- Random sample from one population\n- Question: Are the two variables associated?\n\nTest for Homogeneity:\n- Multiple samples, one from each population\n- Compare distribution of one variable across populations\n- Question: Do populations have same distribution?\n\nMath is identical, but study design differs!' :
                   i === 2 ? 'Total: 150. Success: 90, Fail: 60\nExpected success for each diet: 50(90)/150 = 30 for Diet 1, etc.\nχ² = (30-30)²/30 + (20-20)²/20 + (35-30)²/30 + (15-20)²/20 + (25-30)²/30 + (25-20)²/20\n= 0 + 0 + 0.833 + 1.25 + 0.833 + 1.25 = 4.17\ndf = 2, P-value ≈ 0.124\nP > 0.05, no evidence success rates differ.' :
                   'Solution with test for homogeneity.',
            steps: [
              'H₀: Distribution of variable is same across all populations',
              'Calculate expected counts same way as independence test',
              'χ² = Σ[(O-E)²/E]',
              'df = (rows-1)(columns-1)',
              'Calculations identical to independence test',
              'Difference is in study design and interpretation'
            ]
          }))
        }
      ]
    }
  ]
};

export const unit9: Unit = {
  id: 'unit9',
  title: 'Unit 9: Inference for Quantitative Data: Slopes',
  description: 'Confidence intervals and tests for the slope of a regression line',
  weight: '2%–5%',
  topics: [
    {
      id: 'inference-slope',
      title: 'Inference for Slope',
      description: 'Testing and estimating slopes of regression lines',
      skills: [
        {
          id: 'conditions-linear-regression',
          name: 'Conditions for Inference about Slope',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `slpcond-${i + 1}`,
            question: i === 0 ? 'List and explain the four conditions (LINE) for inference about regression slope.' :
                     i === 1 ? 'A residual plot shows a curved pattern. Can we proceed with inference for slope? Why or why not?' :
                     i === 2 ? 'How do you check the "Equal variance" condition for regression?' :
                     `Assess conditions for regression inference using ${['residual plots', 'normal probability plot', 'scatterplot'][i % 3]}.`,
            answer: i === 0 ? 'LINE conditions:\nL - Linear: Relationship between x and y is linear (check scatterplot, residual plot should show random scatter)\nI - Independent: Observations are independent (random sampling, or < 10% of population)\nN - Normal: For any x, the y-values are normally distributed (check normal probability plot of residuals, or n ≥ 30)\nE - Equal variance: Variability of y is constant for all x (residual plot should show constant vertical spread)' :
                   i === 1 ? 'No, we cannot proceed. The curved pattern violates the Linear condition. The relationship is not linear, so the linear regression model is inappropriate. Must transform data or use nonlinear model.' :
                   i === 2 ? 'Check residual plot: vertical spread of residuals should be roughly constant across all x-values. If spread increases or decreases with x (fan shape), equal variance condition is violated. This is called heteroscedasticity.' :
                   'Solution explaining regression conditions.',
            steps: [
              'Check Linear: scatterplot and residual plot',
              'Check Independent: sampling method',
              'Check Normal: normal probability plot of residuals',
              'Check Equal variance: residual plot vertical spread',
              'All must be satisfied for valid inference'
            ]
          }))
        },
        {
          id: 't-test-slope',
          name: 't-Test for Slope',
          difficulty: 'Hard',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `slptest-${i + 1}`,
            question: i === 0 ? 'Regression: ŷ = 15 + 2.5x, SE_b = 0.8, n = 20. Test H₀: β = 0 vs Hₐ: β ≠ 0 at α = 0.05.' :
                     i === 1 ? 'What does it mean to test H₀: β = 0? What would we conclude if we reject this null?' :
                     i === 2 ? 'Given: b = 3.2, SE_b = 1.5, n = 15. Test if slope is significantly positive at α = 0.05.' :
                     `Perform t-test for slope with n=${Math.floor(Math.random() * 20) + 15}.`,
            answer: i === 0 ? 'H₀: β = 0 vs Hₐ: β ≠ 0 (two-tailed)\nb = 2.5, SE_b = 0.8, df = n-2 = 18\nt = (b - 0) / SE_b = 2.5 / 0.8 = 3.125\nP-value = 2·P(t > 3.125) with df=18 ≈ 2(0.003) = 0.006\nP < 0.05, reject H₀. Strong evidence of a linear relationship. The slope is significantly different from zero.' :
                   i === 1 ? 'Testing H₀: β = 0 tests whether there is a linear relationship between x and y in the population. β = 0 means no linear relationship (horizontal line).\nRejecting H₀ means: There is convincing evidence of a linear relationship. Changes in x are associated with changes in y. The regression line has a non-zero slope.' :
                   i === 2 ? 'H₀: β = 0 vs Hₐ: β > 0 (one-tailed)\ndf = 15-2 = 13\nt = 3.2 / 1.5 ≈ 2.13\nP-value = P(t > 2.13) with df=13 ≈ 0.027\nP < 0.05, reject H₀. Evidence that slope is significantly positive.' :
                   'Solution with t-test for slope.',
            steps: [
              'Check LINE conditions',
              'State hypotheses about β (population slope)',
              'Calculate t = (b - 0) / SE_b',
              'df = n - 2',
              'Find P-value from t-distribution',
              'Make decision and interpret in context'
            ]
          }))
        },
        {
          id: 'ci-slope',
          name: 'Confidence Interval for Slope',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `slpci-${i + 1}`,
            question: i === 0 ? 'Regression line has b = 4.2, SE_b = 1.1, n = 25. Construct 95% CI for β. (t* = 2.069 for df=23)' :
                     i === 1 ? 'A 95% CI for slope is (1.2, 5.8). Interpret this interval.' :
                     i === 2 ? 'Given: b = -2.5, SE_b = 0.9, n = 20. Construct 90% CI for β. (t* = 1.734 for df=18)' :
                     `Construct ${['95%', '90%', '99%'][i % 3]} CI for regression slope.`,
            answer: i === 0 ? 'b = 4.2, SE_b = 1.1, df = 23, t* = 2.069\n95% CI: b ± t*·SE_b = 4.2 ± 2.069(1.1) = 4.2 ± 2.276 = (1.924, 6.476)\nWe are 95% confident that for each one-unit increase in x, the mean value of y increases by between 1.92 and 6.48 units.' :
                   i === 1 ? 'We are 95% confident that the true slope of the regression line (β) is between 1.2 and 5.8. For each one-unit increase in the explanatory variable, we expect the response variable to increase by between 1.2 and 5.8 units on average. Since the entire interval is positive, there is evidence of a positive linear relationship.' :
                   i === 2 ? '90% CI: -2.5 ± 1.734(0.9) = -2.5 ± 1.561 = (-4.061, -0.939)\nWe are 90% confident the true slope is between -4.06 and -0.94. For each unit increase in x, y decreases by 0.94 to 4.06 units on average.' :
                   'Solution with confidence interval for slope.',
            steps: [
              'Check LINE conditions',
              'Find b and SE_b from regression output',
              'df = n - 2',
              'Find t* for confidence level',
              'CI: b ± t*·SE_b',
              'Interpret: change in y per unit change in x'
            ]
          }))
        },
        {
          id: 'slope-interpretation',
          name: 'Interpreting Computer Regression Output',
          difficulty: 'Medium',
          questions: Array(30).fill(null).map((_, i) => ({
            id: `slpint-${i + 1}`,
            question: i === 0 ? 'Computer output shows: Coef=3.5, SE=0.7, t=5.0, P=0.001. Interpret these values.' :
                     i === 1 ? 'Output shows s = 12.5 (standard error of residuals). What does this value tell us?' :
                     i === 2 ? 'R-Sq = 78.5% appears in output. Interpret this value.' :
                     `Interpret regression output with ${['coefficient', 'R-squared', 's value'][i % 3]} provided.`,
            answer: i === 0 ? 'Coef (b) = 3.5: Estimated slope. For each 1-unit increase in x, predicted y increases by 3.5 units.\nSE = 0.7: Standard error of slope. Measures precision of slope estimate.\nt = 5.0: Test statistic for H₀: β = 0. Calculated as t = b/SE = 3.5/0.7 = 5.0\nP = 0.001: Very strong evidence against H₀. Slope is significantly different from zero.' :
                   i === 1 ? 's = 12.5 is the standard deviation of the residuals. It represents the typical distance observations fall from the regression line. Smaller s indicates better fit. Units are same as y-variable.' :
                   i === 2 ? 'R-Sq = 78.5% means 78.5% of the variation in the response variable is explained by the linear relationship with the explanatory variable. This is r² (coefficient of determination). 78.5% is a strong fit; 21.5% of variation remains unexplained.' :
                   'Solution interpreting regression output.',
            steps: [
              'Slope (b): change in ŷ per unit change in x',
              'SE of slope: precision of estimate',
              't-statistic: evidence against H₀: β = 0',
              'P-value: strength of evidence',
              's: typical residual size',
              'R²: proportion of variation explained'
            ]
          }))
        }
      ]
    }
  ]
};